import { LoadingButton } from "@mui/lab";
import { Box, Grid, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import {
  CheckoutState,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext } from "react";
import { AddOnItemName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCheckout } from "components/StripeCheckout/useCheckout";
import SubscriptionButton from "features/Settings/SubscriptionButton";
import { formatEpochTimestamp } from "utils/time";
import { getParams, getSource } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import Subscription from "../Subscription";

export default function EmailVolumeLimitView() {
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    subscription,
    isVaPlan,
    isAutopilotPlan,
    tab,
    upgradeDialogSource,
    addOns,
    setTab,
    setOpenCheckoutDialog,
  } = useContext(SubscriptionContext);
  const { canPurchaseMoreEmailVolume } = useCheckout();
  const { checkoutLoading } = useSubscription();

  const itemName = currentOrg?.isVaUser
    ? AddOnItemName.email_volume_monthly
    : AddOnItemName.email_volume_pro_monthly;
  const canPurchase = canPurchaseMoreEmailVolume(itemName, 1);

  if (canPurchase) {
    const isAutomated = itemName === AddOnItemName.email_volume_monthly;
    const addOn = addOns?.[itemName];
    const extraEmails = isAutomated ? 150 : 50;

    const handlePurchase = () => {
      setTab(SubscriptionTab.monthly);
      const state: CheckoutState = {
        plan: itemName,
        source: getSource(),
        pageSource: window.location.pathname,
        coupon: getParams("coupon"),
      };
      setOpenCheckoutDialog(state);
    };

    return (
      <>
        <Typography sx={{ fontSize: { xs: 20, md: 26 } }} textAlign="center">
          <Box
            component="i"
            className="fa-solid fa-triangle-exclamation"
            sx={{ mr: 1.5, color: "warning.main" }}
          />
          You've reached your email limit
        </Typography>
        <Typography paragraph textAlign="center" sx={{ mt: 1 }}>
          You've used all <strong>{subscription?.monthlyLimit} emails</strong>{" "}
          included in your plan this cycle. Add more to keep reaching out.
        </Typography>

        <Box
          sx={{
            maxWidth: 440,
            mx: "auto",
            mb: 2,
            p: 3,
            borderRadius: 3,
            backgroundColor: "#f0fdf4",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            Add {extraEmails} {isAutomated ? "automated " : ""}emails per month
          </Typography>
          {addOn?.price !== undefined && (
            <Typography sx={{ color: "green.main", fontWeight: 700, mb: 1 }}>
              ${addOn.price.toFixed(2)}/mo
            </Typography>
          )}
          <Typography sx={{ color: "text.secondary", fontSize: 14, mb: 2 }}>
            Credits apply right away and renew with your plan.
            {addOn?.max_quantity
              ? ` Up to ${addOn.max_quantity} packages per account — save up to 33% paying annually.`
              : ""}
          </Typography>
          <Box sx={{ textAlign: "center" }}>
            <LoadingButton
              variant="contained"
              disableElevation
              onClick={handlePurchase}
              loading={checkoutLoading === itemName}
            >
              Add email volume
            </LoadingButton>
          </Box>
        </Box>
      </>
    );
  }

  const showAutopilot = currentOrg?.showAutopilotPlan;
  const isOnTopTier = isVaPlan || (isAutopilotPlan && showAutopilot);
  const isYearly = tab === SubscriptionTab.yearly;
  const premiumPlan = showAutopilot
    ? isYearly
      ? Plans.AUTOPILOT_YEARLY
      : Plans.AUTOPILOT
    : isYearly
      ? Plans.VA_YEARLY
      : Plans.VA;

  const resetDate = subscription?.creditsCycle?.[1]
    ? formatEpochTimestamp({
        epochTimestamp: subscription.creditsCycle[1],
        addYear: true,
      })
    : null;

  const resetSentence = resetDate ? (
    <>
      Your emails reset on <strong>{resetDate}</strong>.
    </>
  ) : (
    "Your emails reset at the start of your next billing cycle."
  );

  return (
    <>
      <Typography sx={{ fontSize: { xs: 20, md: 26 } }} textAlign="center">
        <Box
          component="i"
          className="fa-solid fa-triangle-exclamation"
          sx={{ mr: 1.5, color: "warning.main" }}
        />
        You've hit your email limit for this month
      </Typography>
      <Typography paragraph textAlign="center" sx={{ mt: 1 }}>
        {isOnTopTier ? (
          <>
            You're on our highest plan with the maximum email add-ons, so
            there's no more volume to add right now. {resetSentence}
          </>
        ) : (
          <>
            You've used all your emails for this billing cycle. {resetSentence}{" "}
            To keep sending now, upgrade to a higher plan below.
          </>
        )}
      </Typography>
      {!isOnTopTier && (
        <>
          <Grid sx={{ mb: 3 }} container justifyContent="center">
            <SubscriptionButton />
          </Grid>
          <Subscription
            upgradeDialogSource={upgradeDialogSource}
            plansOverride={[premiumPlan]}
          />
        </>
      )}
    </>
  );
}
